import { apiClient, getAuthHeaders } from "@/services/api.client"
import { BASE, httpError, oiaUrl } from "./stream"

import type {
  CaseDraftRow, DocumentRow, DraftSnapshot, DraftWire, SectionVersion, SimMode, ThreadRow, ThreadSnapshot,
} from "./contract"

const enc = encodeURIComponent

/** A draft started from a Nira case file carries its id, and that case file's home lists it. */
export const createThread = (title: string, mode: SimMode, caseId?: string, selectedDocIds?: string[]) =>
  apiClient.post<ThreadRow>(`${BASE}/threads`, {
    title, mode,
    ...(caseId ? { case_id: caseId } : {}),
    // Only the documents the reader chose for this draft; absent: every document of the workspace.
    ...(caseId && selectedDocIds ? { selected_doc_ids: selectedDocIds } : {}),
  })

/** A draft started from the bundled sample case (made-up noticee): always free, never metered. */
export const createSample = () => apiClient.post<ThreadRow>(`${BASE}/threads/sample`, {})

/** Delete a case. 204 on success; a 404 (unknown id) rejects with `status === 404`. */
export const deleteThread = (threadId: string) =>
  apiClient.delete<void>(`${BASE}/threads/${enc(threadId)}`)

/** Newest first. Backs the history list in the rail. */
export const listThreads = (limit = 50, signal?: AbortSignal) =>
  apiClient.get<ThreadRow[]>(`${BASE}/threads`, { limit: String(limit) }, signal)

/** The drafts started from one case file that a turn ran on, newest first, with their landed stages. */
export const listForCase = (caseId: string, signal?: AbortSignal) =>
  apiClient.get<CaseDraftRow[]>(`${BASE}/threads`, { case_id: caseId }, signal)

export const getThread = (threadId: string, signal?: AbortSignal) =>
  apiClient.get<ThreadSnapshot>(`${BASE}/threads/${enc(threadId)}`, undefined, signal)

/** Start a turn. Resolves with the run id. A 409 (a run is already going) rejects with `status === 409`. */
export const postTurn = (threadId: string, message: string, requestId: string) =>
  apiClient.post<{ run_id: string }>(`${BASE}/threads/${enc(threadId)}/turns`, { request_id: requestId, message }, {
    headers: { "X-Request-Id": requestId, "X-Session-Id": threadId },
  })

/** The guided switch: pause at each stage, or run through. The next turn runs in the new mode. */
export const setMode = (threadId: string, mode: SimMode) =>
  apiClient.patch<ThreadSnapshot>(`${BASE}/threads/${enc(threadId)}`, { mode })

/** Change one row on the decisions page; the engine re-runs only what reads it. */
export const changeRow = (threadId: string, stage: string, rowId: string, to: string, why: string) =>
  apiClient.post<{ run_id: string; rerun: { from_stage: string; stages: string[]; sections: string[]; estimate_ms: number } }>(
    `${BASE}/threads/${enc(threadId)}/rows/${enc(rowId)}`, { stage, to, why },
  )

export const uploadDocument = (threadId: string, file: File) => {
  const form = new FormData()
  form.append("file", file)
  return apiClient.upload<DocumentRow>(`${BASE}/threads/${enc(threadId)}/documents`, form)
}

export const listDocuments = (threadId: string, signal?: AbortSignal) =>
  apiClient.get<DocumentRow[]>(`${BASE}/threads/${enc(threadId)}/documents`, undefined, signal)

/** The server sends sections as a list; the store keys them by section id. Any other shape is a contract break. */
export function draftFromWire(wire: DraftWire): DraftSnapshot {
  if (!wire || typeof wire.version !== "number" || !Array.isArray(wire.sections)) {
    throw new TypeError("GET /draft: expected { version, sections: [...] }")
  }
  const sections: DraftSnapshot["sections"] = {}
  for (const { section_id, ...rest } of wire.sections) sections[section_id] = rest
  const expected = Array.isArray(wire.expected) ? wire.expected : null
  return wire.titles && typeof wire.titles === "object"
    ? { version: wire.version, sections, titles: wire.titles, expected }
    : { version: wire.version, sections, expected }
}

export const getDraft = async (threadId: string, signal?: AbortSignal) =>
  draftFromWire(await apiClient.get<DraftWire>(`${BASE}/threads/${enc(threadId)}/draft`, undefined, signal))

export type PutSectionResult =
  | { ok: true; version: number }
  | { ok: false; conflict: true; currentMarkdown: string; currentVersion: number }

/**
 * Save one section against the version the editor started from. A 409 is a normal outcome, not an error:
 * it carries the server's current markdown and version so the caller can show both sides.
 */
export async function putSection(
  threadId: string,
  sectionId: string,
  markdown: string,
  baseVersion: number,
  opts?: { keepalive?: boolean },
): Promise<PutSectionResult> {
  const res = await fetch(
    oiaUrl(`/threads/${enc(threadId)}/draft/sections/${enc(sectionId)}`),
    {
      method: "PUT",
      headers: { "Content-Type": "application/json", "X-Session-Id": threadId, ...(await getAuthHeaders()) },
      body: JSON.stringify({ markdown, base_version: baseVersion }),
      credentials: "include",
      ...(opts?.keepalive ? { keepalive: true } : {}),
    },
  )
  if (res.status === 409) {
    const body = (await res.json()) as { current_markdown: string; current_version: number }
    return { ok: false, conflict: true, currentMarkdown: body.current_markdown, currentVersion: body.current_version }
  }
  // The 11th edit on a draft: the gateway's 402 rides on the error, shown inline under the draft (no modal).
  if (res.status === 402) {
    const quota: unknown = await res.json().catch(() => null)
    throw Object.assign(new Error("quota"), { status: 402, quota })
  }
  if (!res.ok) throw await httpError(res)
  const body = (await res.json()) as { version?: unknown }
  if (typeof body.version !== "number") throw new TypeError("PUT section: expected { section_id, version }")
  return { ok: true, version: body.version }
}

export const history = (threadId: string, sectionId: string) =>
  apiClient.get<SectionVersion[]>(`${BASE}/threads/${enc(threadId)}/draft/history/${enc(sectionId)}`)

/** Restore an older version as a new version. Resolves with the new version number. */
export const restore = (threadId: string, version: number, baseVersion: number) =>
  apiClient.post<{ version: number }>(`${BASE}/threads/${enc(threadId)}/draft/restore/${version}`, { base_version: baseVersion })

/** The original uploaded bytes, fetched with the same auth as every other call. Rejects with the HTTP error on non-2xx. */
export async function fetchDocumentContent(threadId: string, docId: string, signal?: AbortSignal): Promise<Blob> {
  const res = await fetch(oiaUrl(`/threads/${enc(threadId)}/documents/${enc(docId)}/content`), {
    headers: { "X-Session-Id": threadId, ...(await getAuthHeaders()) },
    credentials: "include",
    signal,
  })
  if (!res.ok) throw await httpError(res)
  return res.blob()
}
